import axios from 'axios';
import toast from 'react-hot-toast';
import { getApiError } from '../utils/apiError';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

// Render free tier can take 60–120s to wake; keep requests alive long enough.
const API_TIMEOUT_MS = 120_000;
const API_MAX_RETRIES = 3;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: API_TIMEOUT_MS,
});

const isRetriableRequest = (error: {
  code?: string;
  message?: string;
  response?: unknown;
  request?: unknown;
}) =>
  error.code === 'ECONNABORTED' ||
  error.message === 'Network Error' ||
  (!error.response && Boolean(error.request));

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const companySlug = localStorage.getItem('companySlug');
  if (companySlug) config.headers['X-Tenant-Slug'] = companySlug;

  try {
    const tenantRaw = localStorage.getItem('smepro360-tenant');
    if (tenantRaw) {
      const { state } = JSON.parse(tenantRaw);
      if (state?.branchId) config.headers['X-Branch-Id'] = String(state.branchId);
    }
  } catch {}

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as (typeof error.config & { __retryCount?: number }) | undefined;
    if (config && isRetriableRequest(error)) {
      config.__retryCount = (config.__retryCount ?? 0) + 1;
      if (config.__retryCount <= API_MAX_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, 3000 * config.__retryCount!));
        return api(config);
      }
    }

    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const slug = localStorage.getItem('companySlug') || 'default';
      const loginPath = `/${slug}/login`;
      if (!window.location.pathname.endsWith('/login')) {
        window.location.assign(loginPath);
      }
    } else if (error.response?.status === 403) {
      toast.error(getApiError(error, 'Permission denied'));
    } else if (error.response?.status === 422) {
      toast.error(getApiError(error, 'Validation failed — check required fields'));
    } else if (error.response?.status >= 500) {
      toast.error('Server error — please try again');
    }
    return Promise.reject(error);
  }
);

export const downloadExcel = async (url: string, filename: string) => {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  const slug = localStorage.getItem('companySlug');
  if (slug) headers['X-Tenant-Slug'] = slug;
  try {
    const response = await fetch(`${API_BASE}${url}`, { headers });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    toast.success('Export downloaded');
  } catch {
    toast.error('Export failed');
  }
};

export { getApiError };
export default api;
